import { type Editor, type JSONContent } from "@tiptap/core"
import {
  DOMSerializer,
  Fragment,
  DOMParser as PMDOMParser,
  Slice,
  type Node as PMNode,
  type Schema,
} from "@tiptap/pm/model"
import { type SliceType } from "./drag-and-drop"
import { getSliceText } from "./get-text"

// Editor-specific clipboards and related helper functions.

// ----------
// Card clipboard
// ----------

let cardClipboard: {
  /**
   * The copied node's type. Copying a standard also puts it in the card
   * clipboard; Card Menu "Paste" then appends that standard to the card.
   */
  type: "card" | "standard"
  /**
   * PMNode in JSON form.
   * We need to convert to JSON and back because different editors use different Schema objects. */
  nodeJson: JSONContent
} | null = null

/**
 * Writes to the special card clipboard, shared by all editors on the page.
 * Using our own clipboard ensures:
 * - Card paste always pastes a card, even if that's not what's in the OS clipboard.
 * - We can programmatically "paste" without requesting permission to access the clipboard.
 *
 * Also writes to the normal clipboard, for compatibility with Mod-V and other programs.
 *
 * @param type Normally "card", but copying a standard also puts it in the card
 * clipboard; Card Menu "Paste" then appends that standard to the card.
 */
export async function setCardClipboard(editor: Editor, type: "card" | "standard", node: PMNode) {
  cardClipboard = { type, nodeJson: node.toJSON() }
  try {
    await copyNodeToClipboard(editor, node)
  } catch (err) {
    // Consider successful even if the OS clipboard write failed - we still have cardClipboard.
    console.error(err)
  }
}

export function getCardClipboard(
  editor: Editor,
): { type: "card" | "standard"; node: PMNode } | null {
  return cardClipboard === null
    ? null
    : { type: cardClipboard.type, node: editor.schema.nodeFromJSON(cardClipboard.nodeJson) }
}

// ----------
// Drag clipboard
//
// Used to share editor.view.dragging.slice between editors, extending ProseMirror's within-editor drag behavior.
// Used by all UnifiedTiptapRenderer editors. Dragging between editors of different
// types (e.g., lesson -> note) is supported, transforming the content with dragClipboardDegradedSlice if needed.
// ----------

export interface DragClipboard {
  sourceEditor: Editor
  /**
   * Slice in JSON form.
   *
   * We need to convert to JSON and back (even between editors with the same extensions)
   * because different editor instances use different Schema objects.
   */
  sliceJSON: unknown
  sliceType: SliceType
  move: boolean
  /**
   * The dragged node in JSON form, when a whole node was dragged rather than a cut of the
   * document. Its type and attrs say what is travelling, so the drop never has to read that
   * back out of slice geometry.
   */
  nodeJSON?: unknown
}

let dragClipboard: DragClipboard | null = null

export function setDragClipboard(clipboard: DragClipboard | null) {
  dragClipboard = clipboard
}

export function getDragClipboard(): DragClipboard | null {
  return dragClipboard
}

/**
 * Materializes the clipboard's slice against the given editor's schema. Never reuse a
 * Slice across editors directly — each editor has its own Schema object, and PM compares
 * node types by identity.
 *
 * Returns null when this schema cannot represent the dragged content at all (editors
 * carry different extension lists — e.g. a card dragged over a note editor): the content
 * simply isn't droppable here.
 */
export function dragClipboardSlice(clipboard: DragClipboard, schema: Schema): Slice | null {
  try {
    return Slice.fromJSON(schema, clipboard.sliceJSON)
  } catch {
    return null
  }
}

/**
 * The single whole node this slice consists of, or null when it is a cut of the document.
 *
 * Both ends must be closed: a slice that starts at a top-level boundary but ends mid-card
 * has `openStart 0` while still being a partial selection.
 */
export function wholeNodeOf(slice: Slice): PMNode | null {
  if (slice.openStart !== 0 || slice.openEnd !== 0) return null
  if (slice.content.childCount !== 1) return null
  return slice.content.firstChild
}

let inMoveDrop = false

/**
 * Whether the drop being processed is relocating content that is already ours.
 *
 * transformPasted normalizes content arriving from outside, which for cards means giving it
 * a fresh identity. A move is the same content changing place, so cards keep theirs — read
 * this in a transformPasted that would otherwise apply a copy's policy.
 */
export function isMoveDrop(): boolean {
  return inMoveDrop
}

/** Runs `handle` with `isMoveDrop()` reporting `moved`. */
export function withDropIntent<T>(moved: boolean, handle: () => T): T {
  const outer = inMoveDrop
  inMoveDrop = moved
  try {
    return handle()
  } finally {
    inMoveDrop = outer
  }
}

/**
 * The nearest representation of the dragged content that `schema` CAN hold: the slice
 * serialized by the source editor's schema, then re-parsed by the target's — the same
 * degradation ProseMirror's own drop performs on the dataTransfer HTML, computed here
 * because dragover can't read the dataTransfer. A card lands in a note editor as its
 * blocks this way, a standard as its citation text. May be empty, when nothing in the
 * content's HTML is readable by this schema.
 */
export function dragClipboardDegradedSlice(clipboard: DragClipboard, schema: Schema): Slice {
  const sourceSchema = clipboard.sourceEditor.schema
  const sourceSlice = Slice.fromJSON(sourceSchema, clipboard.sliceJSON)
  const container = document.createElement("div")
  container.appendChild(
    DOMSerializer.fromSchema(sourceSchema).serializeFragment(sourceSlice.content),
  )
  return PMDOMParser.fromSchema(schema).parseSlice(container)
}

/**
 * The dragged node against the given editor's schema, or null when a cut was dragged —
 * or when this schema has no such node type (see dragClipboardSlice).
 */
export function dragClipboardNode(clipboard: DragClipboard, schema: Schema): PMNode | null {
  if (clipboard.nodeJSON === undefined) return null
  try {
    return schema.nodeFromJSON(clipboard.nodeJSON)
  } catch {
    return null
  }
}

// ----------
// OS clipboard
// ----------

/**
 * Copies a single node to the OS clipboard (as HTML, falling back to plain text),
 * so it can be pasted back into an editor with Mod-V.
 */
export async function copyNodeToClipboard(editor: Editor, node: PMNode): Promise<void> {
  // For non-leaf nodes, skip to the content so we don't get an extra BLOCK_SEPARATOR
  // for the node itself.
  const fragment = node.type.isLeaf ? Fragment.from(node) : node.content
  await copySliceToClipboard(editor, new Slice(fragment, 0, 0))
}

// ----------
// Helpers
// ----------

/**
 * Custom clipboard format carrying the copied node's ProseMirror JSON, for programmatic
 * consumers. Custom formats need the "web " prefix and are Chromium-only for now.
 */
const PM_JSON_FORMAT = "web application/json"

/**
 * Copies a Slice to the OS clipboard (as HTML, falling back to plain text),
 * so it can be pasted back into an editor with Mod-V.
 */
async function copySliceToClipboard(editor: Editor, slice: Slice): Promise<void> {
  // Based on Tiptap UI copy-to-clipboard-button.

  const serialized = editor.view.serializeForClipboard(slice)
  const htmlContent = serialized.dom.innerHTML
  // Use this instead of ProseMirror's serialized.text to make use of Tiptap's renderText functions.
  const textContent = getSliceText(editor, slice)

  try {
    if (htmlContent && navigator.clipboard && "write" in navigator.clipboard) {
      const items: Record<string, Blob> = {
        "text/html": new Blob([htmlContent], { type: "text/html" }),
        "text/plain": new Blob([textContent], { type: "text/plain" }),
      }
      // ClipboardItem.supports is missing from some TS lib versions.
      const ctor = ClipboardItem as typeof ClipboardItem & {
        supports?: (type: string) => boolean
      }
      if (ctor.supports?.(PM_JSON_FORMAT)) {
        items[PM_JSON_FORMAT] = new Blob([JSON.stringify(serialized.slice.toJSON())], {
          type: "application/json",
        })
      }
      await navigator.clipboard.write([new ClipboardItem(items)])
      return
    }
  } catch {
    // Fall through to the plain-text write below.
  }
  await navigator.clipboard.writeText(textContent)
}
